// Central definition of the 4 administrative levels this unified dashboard serves.
//
// Background: the backend's User model already stores `level` (Block / District /
// State / Cluster) on every staff account and returns it un-redacted from the
// `/api/auth/v{1,2,3,5}/:userId/:ttl` "whoami" endpoints (userFindById only strips
// role/email/_id/isDeleted/sessionID/token — level survives). Each of the 4 legacy
// dashboards hardcoded which whoami version to call, which is why they had to be
// separate apps: v5 (Cluster) 401s a District user, v2 (District) 401s a Block user,
// etc. See resolveUserLevel() in levelSession.ts for how the unified app probes
// across versions instead of assuming one.
//
// This file is the map every other level-aware piece of the app should read from,
// instead of hardcoding "cluster"/"district"/etc. inline.

export type Level = "State" | "District" | "Cluster" | "Block";

export const ALL_LEVELS: Level[] = ["State", "District", "Cluster", "Block"];

export interface LevelConfigEntry {
  /** Exact value of User.level on the backend for this tier. */
  level: Level;
  /** Human label used in menus/breadcrumbs/page titles. */
  label: string;
  /** Legacy org-unit noun used in this level's existing UI copy/labels. */
  unitLabel: string;
  /** Route the user lands on immediately after login. */
  homeRoute: string;
  /** `/api/auth/v{n}` suffix used by the whoami endpoint for this level's role. */
  authApiVersion: 1 | 2 | 3 | 5;
  /** Path prefix used by this level's business-data endpoints, e.g. `v5/cluster`. */
  apiPrefix: string;
  /** The level immediately above this one, or null at the top (State). Used for
   *  "forward roster to next level" features. */
  parentLevel: Level | null;
  /** The level immediately below this one, or null at the bottom (Block, which
   *  forwards from School). Used for "review submissions from below" features. */
  childLevel: Level | null;
}

export const LEVEL_CONFIG: Record<Level, LevelConfigEntry> = {
  State: {
    level: "State",
    label: "State",
    unitLabel: "State",
    // Was "/dashboard/state" (the old State Profile page) - the stat-card/
    // chart State Dashboard (statedashboard/stateDashboard/index.tsx) is now
    // the landing page instead, per the 16 Sept dev-plan. State Profile's
    // route/menu item still exist (just hidden from the menu, see
    // appMenu.ts), reachable directly by URL.
    homeRoute: "/dashboard/state-stats",
    authApiVersion: 3,
    apiPrefix: "v3/state",
    parentLevel: null,
    childLevel: "District",
  },
  District: {
    level: "District",
    label: "District",
    unitLabel: "District",
    // Was "/dashboard/district" (the old District Profile page) - the
    // stat-card/chart District Dashboard (districtdashboard/districtDashboard/
    // index.tsx) is now the landing page instead, per the 16 Sept dev-plan.
    // District Profile's route/menu item still exist (just hidden from the
    // menu, see appMenu.ts), reachable directly by URL.
    homeRoute: "/dashboard/district-stats",
    authApiVersion: 2,
    apiPrefix: "v2/district",
    parentLevel: "State",
    childLevel: "Cluster",
  },
  Cluster: {
    level: "Cluster",
    label: "Cluster",
    unitLabel: "CRC",
    // Was "/dashboard/crc" (the old CRC Profile page) - the stat-card/chart
    // CRC Dashboard (crcDashboard/index.tsx) is now the landing page instead,
    // per the 14 Sept dev-plan. CRC Profile's route/menu item still exist
    // (just hidden from the menu, see appMenu.ts), reachable directly by URL.
    homeRoute: "/dashboard/crc-stats",
    authApiVersion: 5,
    apiPrefix: "v5/cluster",
    parentLevel: "District",
    childLevel: "Block",
  },
  Block: {
    level: "Block",
    label: "Block",
    unitLabel: "Block",
    // Was "/dashboard/block" (the old Block Profile page) - the stat-card/
    // chart Block Dashboard (blockdashboard/blockDashboard/index.tsx) is now
    // the landing page instead, per the 16 Sept dev-plan. Block Profile's
    // route/menu item still exist (just hidden from the menu, see
    // appMenu.ts), reachable directly by URL.
    homeRoute: "/dashboard/block-stats",
    authApiVersion: 1,
    apiPrefix: "v1/block",
    parentLevel: "Cluster",
    childLevel: null,
  },
};

/** Order to probe whoami endpoints in when the user's level isn't known yet
 *  (right after login). Cluster first since this app was cluster-only historically. */
export const LEVEL_PROBE_ORDER: Level[] = ["Cluster", "District", "Block", "State"];

export function isLevel(value: unknown): value is Level {
  return typeof value === "string" && (ALL_LEVELS as string[]).includes(value);
}

export function getLevelConfig(level: Level | string | undefined | null): LevelConfigEntry | null {
  if (!isLevel(level)) return null;
  return LEVEL_CONFIG[level];
}
